/**
 * Every tunable number in KNGDM.IO, in one file.
 *
 * Nothing here is sacred. These are first-pass values chosen to make the loop
 * playable, not values validated by playtesting — Phase 11 exists to replace
 * them with numbers backed by data from real sessions. Keeping them in a single
 * module (rather than scattered through the logic that uses them) is what makes
 * that pass tractable, and what lets the headless AI-vs-AI runner sweep them.
 *
 * Rates are expressed **per second** and converted to per-tick by the
 * simulation, so changing the tick rate doesn't silently rebalance the game.
 */
import { BIOMES } from "./messages.js";
// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------
/** docs/01 targets 30-45 minute sessions, matching OpenFront's pacing. */
export const DEFAULT_SESSION_SECONDS = 35 * 60;
export const DEFAULT_MAP_WIDTH = 40;
export const DEFAULT_MAP_HEIGHT = 40;
/**
 * Kingdoms in a default free-for-all, player included. Open question #9 in the
 * roadmap — decide empirically with the headless runner, since it changes
 * territory per kingdom, how early borders touch, and session length.
 */
export const DEFAULT_KINGDOM_COUNT = 6;
// ---------------------------------------------------------------------------
// Resource yields
// ---------------------------------------------------------------------------
/**
 * Base yield per owned hex, per second, at full worker coverage.
 *
 * The shape of this table is the whole "territory matters qualitatively" idea
 * from docs/01: no biome is strictly better than another, so *which* hexes you
 * hold decides what you can afford to build, not just how many.
 */
export const BIOME_YIELDS = {
    plains: { food: 1.0, timber: 0.34, ore: 0.0, gold: 0.34, aether: 0 },
    forest: { food: 0.34, timber: 1.0, ore: 0.0, gold: 0.34, aether: 0 },
    hills: { food: 0.34, timber: 0.34, ore: 0.67, gold: 0.34, aether: 0 },
    mountains: { food: 0.0, timber: 0.0, ore: 1.17, gold: 0.34, aether: 0 },
    desert: { food: 0.0, timber: 0.0, ore: 0.34, gold: 0.84, aether: 0 },
    tundra: { food: 0.34, timber: 0.17, ore: 0.34, gold: 0.34, aether: 0 },
};
// ---------------------------------------------------------------------------
// Special hexes and Aether
// ---------------------------------------------------------------------------
/**
 * Per-second Aether yield of each special hex type, and the ordinary resources
 * it throws off alongside.
 *
 * Aether is deliberately *slow*. A god tool should feel like a decision you save
 * up for over minutes, not a rotation you cycle; at these rates a single held
 * geyser funds roughly one terraform every two and a half minutes. The three
 * types differ in what they pair the Aether with, so which special hex you fight
 * for is itself a choice: ruins pay gold, geysers are the purest Aether source,
 * crystal veins pay ore.
 */
export const SPECIAL_HEX_YIELDS = {
    ruins: { food: 0, timber: 0, ore: 0, gold: 1.2, aether: 0.16 },
    geyser: { food: 0, timber: 0, ore: 0, gold: 0, aether: 0.28 },
    crystalVein: { food: 0, timber: 0, ore: 1.0, gold: 0, aether: 0.20 },
};
/**
 * Special hexes placed on a default 40x40 map, scaled by area for other sizes.
 *
 * Eight on 1600 hexes is roughly one per two kingdoms at the default count —
 * scarce enough that they cannot all be held, which is the entire point. Make
 * them common and Aether becomes another income line; make them rarer than this
 * and most sessions have kingdoms that never touch one, which reads as the
 * feature not existing rather than as scarcity.
 */
export const SPECIAL_HEXES_PER_1600_HEXES = 8;
/**
 * Minimum distance from any spawn, in hexes.
 *
 * docs/07 Stage 5.1 asks for these to be "contested-by-design". A special hex
 * inside a kingdom's opening ring is the opposite: free Aether decided by the map
 * generator rather than by play. Pushing them out past the starting claim radius
 * means every one of them has to be expanded toward or taken.
 */
export const SPECIAL_HEX_MIN_SPAWN_DISTANCE = 6;
/** Minimum distance between two special hexes, so they don't clump into a prize. */
export const SPECIAL_HEX_MIN_SEPARATION = 5;
/** Glory for holding a special hex at session end, on top of the hex itself. */
export const GLORY_PER_SPECIAL_HEX = 40;
/**
 * Aether has its own cap, and it does not scale with territory.
 *
 * The ordinary cap is `BASE_STORAGE_CAP + hexes * STORAGE_CAP_PER_HEX`, which
 * reaches 2400 on a large kingdom — meaningless for a resource that trickles in
 * at a quarter-point a second. A small flat ceiling is what makes Aether a
 * "spend it on something" resource instead of a savings account: hold two
 * geysers and you will hit this in a couple of minutes, and anything past it is
 * thrown away.
 */
export const AETHER_STORAGE_CAP = 40;
/** Storage cap per kingdom. Uncapped banking removes all urgency to spend. */
export const BASE_STORAGE_CAP = 600;
/** Extra storage per owned hex, so wider kingdoms can bank bigger purchases. */
export const STORAGE_CAP_PER_HEX = 12;
// ---------------------------------------------------------------------------
// Population, workers and troops
// ---------------------------------------------------------------------------
/** Population a kingdom supports before any food production. */
export const BASE_POPULATION_CAP = 20;
/** Population supported per unit of food produced per second. */
export const POPULATION_PER_FOOD_RATE = 2.5;
/** Fraction of the gap to the cap that population closes each second. */
export const POPULATION_GROWTH_RATE = 0.03;
/** Fraction of population lost per second while food is exhausted. */
export const STARVATION_RATE = 0.08;
/**
 * Food eaten per unit of population per second.
 *
 * Raised from 0.02 to drain some of the permanent food surplus recorded in
 * docs/07 ("food sits pinned at its storage cap"). It is a partial fix and
 * deliberately a timid one: population sits inside a feedback loop — population
 * sets workers, workers set yield, yield sets the population cap — so this
 * constant is far more leveraged than it looks. A headless sweep at 0.12 sent
 * every kingdom into a starvation spiral (population 33 -> 12, troops 15 -> 4,
 * combat extinct by minute fourteen); 0.04 cuts food-at-cap from ~70% of samples
 * to ~50% with population and troop counts unchanged.
 *
 * Closing the rest of the gap means restructuring that loop rather than turning
 * this dial, which is Phase 11 work with real session data behind it.
 */
export const FOOD_UPKEEP_PER_POP = 0.04;
/**
 * Workers needed to fully work one hex. Below this the whole kingdom's yield is
 * scaled down — the mechanism that stops "claim everything" being a free win and
 * makes the worker/troop slider a real decision rather than a dial you set once.
 */
export const WORKERS_PER_HEX = 1.0;
/** Worker coverage can't push yield above this multiple of the base rate. */
export const MAX_WORKER_EFFICIENCY = 1.0;
export const DEFAULT_WORKER_RATIO = 0.65;
// ---------------------------------------------------------------------------
// Expansion
// ---------------------------------------------------------------------------
/**
 * Cost to claim one hex of each biome. Mountains eat ore, forest eats timber —
 * docs/01's core twist on OpenFront, pulling Catan's expand-or-build tension
 * into a real-time land grab.
 */
export const EXPANSION_COSTS = {
    plains: { food: 48, timber: 40, ore: 0, gold: 20, aether: 0 },
    forest: { food: 24, timber: 104, ore: 0, gold: 20, aether: 0 },
    hills: { food: 24, timber: 48, ore: 64, gold: 20, aether: 0 },
    mountains: { food: 0, timber: 48, ore: 136, gold: 40, aether: 0 },
    desert: { food: 16, timber: 24, ore: 24, gold: 48, aether: 0 },
    tundra: { food: 40, timber: 24, ore: 24, gold: 20, aether: 0 },
};
/**
 * Each hex already owned raises the cost of the next one by this fraction.
 *
 * Open question #4 in the roadmap. This is the main anti-snowball lever in the
 * game: without it, an early lead compounds into an uncatchable one, which is
 * exactly the failure mode that makes a territorial game boring by minute ten.
 * At 0.018, the 50th hex costs ~1.9x the first and the 100th ~2.8x.
 */
export const EXPANSION_SCALING_PER_HEX = 0.018;
// ---------------------------------------------------------------------------
// Combat
// ---------------------------------------------------------------------------
/**
 * Defensive multiplier applied to troops standing on each biome.
 *
 * This is what makes terrain matter for reasons beyond yield: a mountain range
 * is a wall you can hold cheaply, open plains are indefensible. Holding good
 * ground is a strategy.
 */
export const TERRAIN_DEFENCE = {
    plains: 1.0,
    forest: 1.35,
    hills: 1.6,
    mountains: 2.1,
    desert: 0.95,
    tundra: 1.1,
};
/** Neighbouring friendly hexes contribute this fraction of their troops. */
export const ADJACENT_SUPPORT_FACTOR = 0.35;
/** Capture-meter progress per second per unit of surplus attacking pressure. */
export const CAPTURE_RATE = 0.020;
/**
 * Capture-meter decay per second when a hex is no longer under attack.
 *
 * Deliberately fast relative to CAPTURE_RATE. Without decay, an attacker can
 * park progress on a hex indefinitely and take it later for free; with it, a
 * push you don't sustain is a push you wasted.
 */
export const CAPTURE_DECAY_RATE = 0.06;
/** Troops lost per second by both sides, per unit of pressure in contact. */
export const ATTRITION_RATE = 0.012;
/**
 * A surged border segment pulls this multiple of a normal hex's troop share.
 *
 * Raised from 4.0 to fix the dead endgame recorded in docs/07 ("bots stop
 * attacking late in a session"). The cause was not the bot's surge threshold: it
 * was that attacking genuinely stopped working. A kingdom's frontier grows with
 * its territory (measured: 14 hexes at minute 1, 62 by minute 35) while its troop
 * pool does not, so troops-per-frontier-hex halves over a session — and the
 * defender gets `TERRAIN_DEFENCE` plus `ADJACENT_SUPPORT_FACTOR` on top. The
 * headless runner shows the average attack/defence ratio at contested hexes
 * decaying from 1.90 to 0.89, crossing below parity at minute 26, which is
 * exactly when the bots stopped surging. They were right to stop; below 1.0 no
 * push can ever fill a capture meter.
 *
 * At 9.0 a committed push concentrates enough of the pool to break a line even
 * when the army is spread thin, and the ratio holds above 1.1 to the final tick.
 * That keeps the *decision* intact rather than making attacking free — a surge
 * this heavy visibly thins the rest of your border, so choosing where to commit
 * is the cost.
 *
 * The tempting fix — raising population so armies scale with territory — was
 * measured and rejected: it re-created the total-conquest failure mode described
 * under NEUTRAL_LAND_CAPTURABLE_BY_PRESSURE, with one kingdom taking 1349 of
 * 1600 hexes.
 */
export const SURGE_WEIGHT = 9.0;
/**
 * Fraction of the gap to its target troop count a hex closes per second.
 *
 * Below 1.0 on purpose: a surge has to *travel*. Instant redeployment would
 * make the one core combat verb a button rather than a decision with a cost.
 */
export const TROOP_REDEPLOY_RATE = 0.25;
/** A hex flipping owner keeps this fraction of the attacker's committed troops. */
export const CAPTURE_TROOP_CARRYOVER = 0.4;
/**
 * Whether unclaimed land can be taken by troop pressure alone.
 *
 * **False, and this is load-bearing.** The first headless AI-vs-AI batch had it
 * true: neutral hexes have no defender, so defence pressure is zero, so any
 * troops at all captured them in about two seconds — on every frontier hex at
 * once. Every one of twelve sessions ended with a single kingdom owning all 1600
 * hexes inside four minutes of a thirty-five minute session.
 *
 * The reason that is fatal rather than merely fast: it makes territory free.
 * docs/01's central tension is Catan's expand-or-build decision layered onto
 * OpenFront's land grab, and that only exists if land costs resources. With a
 * free military path to expansion the optimal play is "build troops, take
 * everything", the economy becomes decoration, and the two systems collapse into
 * one.
 *
 * So the two paths stay distinct: **neutral land is bought with resources
 * (`claimHex`), enemy land is taken with troops (border pressure).**
 */
export const NEUTRAL_LAND_CAPTURABLE_BY_PRESSURE = false;
/**
 * One building for the MVP, per docs/03 — enough to prove terrain gating works.
 * Adding the second should be a new entry here and nothing else; if it isn't,
 * the system is wrong and needs fixing before the list grows.
 */
export const BUILDINGS = {
    // Declaration order is hotbar order: `inputController.ts` and `buildHotbar.ts`
    // both derive keys 1-9 from `Object.keys(BUILDINGS)`, so moving an entry moves
    // the key a player has learned. Append rather than insert.
    sawmill: {
        type: "sawmill",
        label: "Sawmill",
        allowedBiomes: ["forest"],
        cost: { food: 0, timber: 45, ore: 20, gold: 15, aether: 0 },
        yieldMultiplier: { timber: 2.2 },
        defenceMultiplier: 1,
        storageBonus: 0,
        gloryValue: 25,
    },
    quarry: {
        type: "quarry",
        label: "Quarry",
        allowedBiomes: ["hills", "mountains"],
        // Paid for in timber and gold rather than ore: a building that costs the
        // resource it produces is a loan, not a decision.
        cost: { food: 0, timber: 55, ore: 10, gold: 25, aether: 0 },
        yieldMultiplier: { ore: 2.0 },
        defenceMultiplier: 1,
        storageBonus: 0,
        gloryValue: 25,
    },
    farm: {
        type: "farm",
        label: "Farm",
        allowedBiomes: ["plains"],
        // The strongest building in the set, because food is the only resource that
        // feeds back into population, which feeds workers *and* troops. Priced above
        // the others to match.
        cost: { food: 0, timber: 60, ore: 15, gold: 25, aether: 0 },
        yieldMultiplier: { food: 1.8 },
        defenceMultiplier: 1,
        storageBonus: 0,
        gloryValue: 25,
    },
    tradingPost: {
        type: "tradingPost",
        label: "Trading Post",
        // Desert only. Desert is the biome nobody wants — no food, no timber, almost
        // no ore — so gating the gold multiplier to it turns the map's dead ground
        // into somewhere worth claiming, which is exactly what terrain gating is for.
        allowedBiomes: ["desert"],
        cost: { food: 0, timber: 40, ore: 30, gold: 30, aether: 0 },
        yieldMultiplier: { gold: 2.0 },
        defenceMultiplier: 1,
        storageBonus: 0,
        gloryValue: 25,
    },
    walls: {
        type: "walls",
        label: "Walls",
        // Buildable anywhere, because a frontier can form anywhere — gating walls to
        // a biome would mean the hex you most need to hold is the one you cannot
        // fortify. Ore-costed per docs/01, which lists walls as ore's purpose.
        allowedBiomes: BIOMES,
        cost: { food: 0, timber: 30, ore: 70, gold: 10, aether: 0 },
        yieldMultiplier: {},
        // Multiplies on top of TERRAIN_DEFENCE, so walls on mountains reach 3.4x
        // while walls on plains reach 1.6x. That is deliberate: it rewards holding
        // good ground rather than papering over bad ground.
        defenceMultiplier: 1.6,
        storageBonus: 0,
        // Lower than the yield buildings: it produces nothing, and Glory should not
        // pay a kingdom twice for turtling.
        gloryValue: 15,
    },
    storehouse: {
        type: "storehouse",
        label: "Storehouse",
        allowedBiomes: BIOMES,
        cost: { food: 0, timber: 70, ore: 25, gold: 30, aether: 0 },
        yieldMultiplier: {},
        defenceMultiplier: 1,
        // Deliberately large relative to BASE_STORAGE_CAP: the point is to make
        // banking for an expensive push possible, and a token increase would not
        // change any decision. Does **not** raise the Aether cap — that ceiling is
        // flat on purpose (see AETHER_STORAGE_CAP).
        storageBonus: 250,
        gloryValue: 20,
    },
};
/**
 * The events, as data.
 *
 * `migration` has weight 0: a hostile horde crossing the map is a *unit*, and
 * units are Phase 6. It stays in the table so the gap is visible rather than
 * forgotten, and so enabling it later is a weight change plus a unit.
 */
export const WORLD_EVENTS = {
    drought: {
        type: "drought",
        label: "Drought",
        weight: 30,
        radius: 4,
        durationSeconds: 150,
        // Food only. Drought is the event that bites hardest precisely because food
        // caps population, which caps both workers and troops.
        yieldScale: { food: 0.25 },
        transformsBiome: null,
        leavesSpecial: null,
    },
    flood: {
        type: "flood",
        label: "Flood",
        weight: 26,
        radius: 3,
        durationSeconds: 120,
        // Ruins the harvest now, and the silt makes it better afterwards — but the
        // "afterwards" is the biome change below, so the two halves are separate.
        yieldScale: { food: 0.4, timber: 0.6 },
        transformsBiome: "plains",
        leavesSpecial: null,
    },
    eruption: {
        type: "eruption",
        label: "Volcanic eruption",
        weight: 18,
        radius: 2,
        durationSeconds: 90,
        // Near-total during the eruption itself.
        yieldScale: { food: 0.05, timber: 0.05, ore: 0.5, gold: 0.2 },
        // Fertile ash, per docs/01 — the hexes come back better than they were, so a
        // player who holds through the disaster is rewarded rather than just punished.
        transformsBiome: "plains",
        leavesSpecial: null,
    },
    meteor: {
        type: "meteor",
        label: "Meteor strike",
        weight: 14,
        radius: 1,
        // Instantaneous: the crater is the event. docs/01 wants it to trigger a
        // land-grab race, and a race needs the prize to exist immediately.
        durationSeconds: 0,
        yieldScale: {},
        transformsBiome: "mountains",
        leavesSpecial: "crystalVein",
    },
    migration: {
        type: "migration",
        label: "Monster migration",
        // Disabled until Phase 6 gives it something to be.
        weight: 0,
        radius: 3,
        durationSeconds: 120,
        yieldScale: {},
        transformsBiome: null,
        leavesSpecial: null,
    },
};
/** Seconds before the first event can fire, so the opening is never disrupted. */
export const WORLD_EVENT_GRACE_SECONDS = 180;
/** Bounds on the gap between events. The engine rolls uniformly between them. */
export const WORLD_EVENT_MIN_INTERVAL_SECONDS = 100;
export const WORLD_EVENT_MAX_INTERVAL_SECONDS = 220;
/** Most events in flight at once, so a session cannot become all disaster. */
export const MAX_CONCURRENT_WORLD_EVENTS = 3;
/**
 * Aether's only sink, per docs/01.
 *
 * Priced against `AETHER_STORAGE_CAP` (40) and the ~0.2/s a special hex yields:
 * a kingdom holding one geyser affords the cheapest tool roughly every ninety
 * seconds and the dearest every three minutes. That is the intended texture —
 * a god tool should feel like something you saved for, not a rotation.
 */
export const GOD_TOOLS_DEFINITIONS = {
    terraform: {
        tool: "terraform",
        label: "Terraform",
        cost: { food: 0, timber: 0, ore: 0, gold: 0, aether: 22 },
        description: "Turn one hex you own into plains.",
    },
    fortify: {
        tool: "fortify",
        label: "Fortify",
        cost: { food: 0, timber: 0, ore: 0, gold: 0, aether: 14 },
        description: "Brace one hex you own against attack for a time.",
    },
    calm: {
        tool: "calm",
        label: "Calm",
        cost: { food: 0, timber: 0, ore: 0, gold: 0, aether: 18 },
        description: "End a world event striking this hex.",
    },
    provoke: {
        tool: "provoke",
        label: "Provoke",
        cost: { food: 0, timber: 0, ore: 0, gold: 0, aether: 30 },
        description: "Call a disaster down on this hex.",
    },
};
/** Defence multiplier a fortified hex gets, on top of terrain and walls. */
export const FORTIFY_DEFENCE_MULTIPLIER = 2.0;
/** How long `fortify` lasts. Short — it is an emergency brace, not a building. */
export const FORTIFY_DURATION_SECONDS = 45;
/** Radius a `provoke` disaster covers, independent of the event's own radius. */
export const PROVOKE_RADIUS = 2;
// ---------------------------------------------------------------------------
// Glory Score
// ---------------------------------------------------------------------------
/**
 * docs/01: Glory Score is what gives a builder/trader a path to victory without
 * ever winning a fight. Territory is weighted, but not so heavily that raw land
 * area beats everything else — that would collapse it back into "who conquered
 * most", which pure OpenFront already does.
 */
export const GLORY_PER_HEX = 10;
export const GLORY_PER_BANKED_RESOURCE = 0.08;
export const GLORY_PER_TRADE_VOLUME = 0.5;
// ---------------------------------------------------------------------------
// Starting conditions
// ---------------------------------------------------------------------------
export const STARTING_RESOURCES = {
    food: 200,
    timber: 200,
    ore: 120,
    gold: 100,
    // None. Every point of Aether in a session is earned by holding a special hex,
    // so a god tool is always evidence that someone won a fight over one.
    aether: 0,
};
export const STARTING_POPULATION = 20;
/** Hexes each kingdom starts with: its spawn plus the ring around it. */
export const STARTING_CLAIM_RADIUS = 1;
//# sourceMappingURL=balance.js.map